import React from 'react';
import useProjects from 'hooks/useProjects';
import { ui } from 'util/ui';
import { TickIcon, ImageIcon } from './shell/Icons';

/**
 * Rows in the Incomplete projects pop-up. UI refresh styling (Aaron, 5 Nov 2026): rounded rows,
 * rounded thumbnail, and a "Mark complete" button in the complete colour with the new tick.
 * Clicking a row opens the project.
 */

type Props = {
  incompleteProjects: IProject[];
  gotoProject(project: IProject): void;
};

export const IncompleteList: React.FC<Props> = ({ incompleteProjects, gotoProject }) => {
  const { updateProject } = useProjects();

  const markComplete = (event: React.MouseEvent, project: IProject) => {
    event.stopPropagation();
    updateProject(project.id, { complete: !project.complete });
  };

  if (incompleteProjects.length === 0) {
    return <p style={{ margin: 0, padding: '32px 16px', textAlign: 'center', fontSize: 15, color: ui.muted }}>No incomplete projects. Nice.</p>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {incompleteProjects.map((project) => (
        <div
          key={project.id}
          role="button"
          tabIndex={0}
          className="archy-row-btn"
          onClick={() => gotoProject(project)}
          onKeyDown={(e) => e.key === 'Enter' && gotoProject(project)}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8, borderRadius: 12, cursor: 'pointer', fontFamily: ui.font }}
        >
          <span
            style={{
              width: 64,
              height: 36,
              flex: 'none',
              borderRadius: 8,
              background: project.featureURL ? `#24506B url("${project.featureURL}") center / cover` : '#24506B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!project.featureURL && <ImageIcon size={16} />}
          </span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 500, color: ui.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {project.title || 'Untitled project'}
            {project.client && <span style={{ color: ui.muted, fontWeight: 400 }}> · {project.client}</span>}
          </span>
          <button
            type="button"
            className="archy-btn"
            title="Mark this project complete"
            onClick={(e) => markComplete(e, project)}
            style={{
              flex: 'none',
              height: 32,
              padding: '0 12px 0 8px',
              border: 'none',
              borderRadius: 8,
              background: ui.complete,
              color: '#FFFFFF',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <TickIcon size={15} />
            Mark complete
          </button>
        </div>
      ))}
    </div>
  );
};
